import { getInitials } from '../../../utils/helpers';
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import './FollowUpModule.css';
import FollowUpFilterationActions from './FollowUpFilterationActions';
import FollowUpDetails from './FollowUpDetails';
import Panels from '../../../components/common/Panels/Panels';
import GridMainPanelLayout from '../../../components/common/Panels/GridMainPanelLayout';
import UniversalDetailContent from '../../../components/common/Panels/UniversalDetailContent';
import { useQuery } from '@tanstack/react-query';
import {
    PhoneCall, Mail, MessageSquare, Mic,
    FileText, Calendar, CalendarCheck, User, Building, Plus, Settings, Check, Search, Grid, List, MoreHorizontal, LayoutGrid,
    Filter, Users, Wallet, Upload, CheckSquare, Percent, Edit3, ExternalLink, ChevronDown, Wifi,
    AlertCircle, Clock, CalendarX, Maximize2, Download, X
} from 'lucide-react';
import { EntityEdit } from './EntityEdit';
import Swal from 'sweetalert2';
import API_ENDPOINTS from '../../../config/apiEndpoints';
import DynamicCalendar from '../../../components/DynamicCalendar/DynamicCalendar';
import Loader from '../../../components/Loader/Loader';
import CallWidget from '../../../components/common/CallWidget/CallWidget';
import FollowUpList from './followup/list';
import { LeadDetails as CommonLeadDetails } from '../../../components/common/LeadDetails/LeadDetails';
import PremiumModuleCards from '../../Task/Components/common/PremiumModuleCards';
import { Icon } from '@iconify/react';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Avatar from '@mui/material/Avatar';
import { formatDate, getStatusDescriptor } from '../../Task/Components/common/premiumUtils';
import SubHeader from '../../../components/common/SubHeader/SubHeader';
import Chip from '@mui/material/Chip';

import { useLocation, useNavigate } from 'react-router-dom';
import { getSession } from '../../../getSession';
import { getUserListByUserId } from '../../../utils/lead';
import usePermissions from '../../Master_Settings/usePermissions';
import useFieldMasking from '../../Master_Settings/useFieldMasking';

const formatDateTime = (dateStr) => {
    if (!dateStr) return '--';
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr || '--';
    return date.toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
    });
};


const renderCalendarEvent = (event) => {
    const fu = event.extendedProps || {};
    const status = (fu._statusClass || 'scheduled').toLowerCase();

    const colors = {
        overdue: { main: '#ef4444', bg: '#fef2f2', text: '#ef4444' },
        'due-today': { main: '#22c55e', bg: '#f0fdf4', text: '#22c55e' },
        scheduled: { main: '#f59e0b', bg: '#fffbeb', text: '#f59e0b' },
        'no-followup': { main: '#3b82f6', bg: '#eff6ff', text: '#3b82f6' }
    };

    const color = colors[status] || colors.scheduled;
    const name = fu.PersonName || 'Unknown';
    const initials = getInitials(name);

    return (
        <Box
            sx={{
                p: '6px 10px',
                borderRadius: '8px',
                bgcolor: color.bg,
                borderLeft: `3px solid ${color.main}`,
                transition: 'all 0.2s',
                display: 'flex',
                flexDirection: 'column',
                gap: '6px',
                width: '100%',
                boxSizing: 'border-box',
                '&:hover': {
                    filter: 'brightness(0.97)',
                    transform: 'translateX(2px)',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
                }
            }}
        >
            {/* Row 1: Followup Status/Title */}
            <Typography sx={{
                fontSize: '12.5px',
                fontWeight: 700,
                color: '#2563eb',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                width: '100%'
            }}>
                {fu.FollowupStatus || 'Follow-up'}
            </Typography>

            {/* Row 2: Lead and Agent Info */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '4px', width: '100%' }}>
                {/* Related To (Lead) */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', flex: 1, minWidth: 0 }}>
                    <Avatar sx={{
                        width: 26, height: 26, fontSize: '11px', fontWeight: 700,
                        bgcolor: '#f1f5f9', color: '#64748b', border: '1px solid #e2e8f0',
                        flexShrink: 0
                    }}>
                        {initials}
                    </Avatar>
                    <Typography sx={{
                        fontSize: '11px', fontWeight: 600, color: '#475569',
                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                        minWidth: 0
                    }}>
                        {name}
                    </Typography>
                </Box>

                {/* Agent Info */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', flex: 1, minWidth: 0, justifyContent: 'flex-end' }}>
                    <Box sx={{
                        width: 20, height: 20, borderRadius: '50%', border: '1px solid #e2e8f0',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#fff',
                        flexShrink: 0
                    }}>
                        <Icon icon="ri:user-3-line" fontSize={11} color="#94a3b8" />
                    </Box>
                    <Typography sx={{
                        fontSize: '11px', fontWeight: 500, color: '#64748b',
                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                        minWidth: 0
                    }}>
                        {fu.AgentName || '-'}
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
};

const getFollowUpBadge = (fu) => {
    const statusLabel = getStatusDescriptor(
        fu.taskStatus,
        fu.endDate,
        fu.completedDate,
        fu.taskTitle,
        fu.startDate,
        fu.statusLabel,
        fu.outcomeName,
        fu.createdDate
    );

    let chipColor = 'info';
    const lowerLabel = statusLabel.toLowerCase();
    if (lowerLabel.includes('overdue')) chipColor = 'error';
    else if (lowerLabel.includes('due today')) chipColor = 'success';
    else if (lowerLabel.includes('due in') || lowerLabel.includes('scheduled')) chipColor = 'warning';
    else if (lowerLabel.includes('completed')) chipColor = 'success';

    return (
        <Chip
            label={statusLabel}
            color={chipColor}
            variant='filled'
            size='small'
            className='font-medium text-[10px] h-[20px] px-2'
            sx={{
                color: '#fff !important',
                backgroundColor: (theme) => {
                    if (chipColor === 'error') return '#ef4444';
                    if (chipColor === 'success') return '#22c55e';
                    if (chipColor === 'warning') return '#f59e0b';
                    if (chipColor === 'info') return '#3b82f6';
                    return undefined;
                }
            }}
        />
    );
};

const FollowUpCalendarLegend = () => (
    <Stack direction="row" spacing={2.5} sx={{ mt: 1.5, flexWrap: 'wrap', gap: '12px', px: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#22c55e' }} />
            <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#475569' }}>Due Today</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#ef4444' }} />
            <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#475569' }}>Overdue</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#f59e0b' }} />
            <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#475569' }}>Scheduled</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#3b82f6' }} />
            <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#475569' }}>No Followup</Typography>
        </Box>
    </Stack>
);

const FollowUpModule = () => {
    const location = useLocation();
    const navigate = useNavigate();

    const { permissions } = usePermissions('PAG10039');
    const { maskObject, isHiddenField } = useFieldMasking('PAG10372#PAG10039');
    const followupPerms = useMemo(() => {
        if (!permissions || !Array.isArray(permissions)) return null;
        return permissions.find(p => p.ModuleCode?.includes('PAG10039')) || {};
    }, [permissions]);

    const [pipelines, setPipelines] = useState([]);
    const [dashboardFilter, setDashboardFilter] = useState(location.state?.dashboardFilter || null);
    const [filterLabel, setFilterLabel] = useState(location.state?.filterLabel || null);

    // State Management
    const [followUps, setFollowUps] = useState([]);
    const [isInitialLoad, setIsInitialLoad] = useState(true);
    const [customSearchId, setCustomSearchId] = useState(0);
    const [totalRecords, setTotalRecords] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(100);
    const [viewMode, setViewMode] = useState('table');
    const [selectedFollowUp, setSelectedFollowUp] = useState(null);
    const [selectedSplitId, setSelectedSplitId] = useState(null);
    const [selectedGridFollowUp, setSelectedGridFollowUp] = useState(null);
    const [showDetails, setShowDetails] = useState(false);
    const [showLeadProfile, setShowLeadProfile] = useState(false);
    const [selectedProfileLead, setSelectedProfileLead] = useState(null);
    const [showEditModal, setShowEditModal] = useState(false);
    const [selectedEditLead, setSelectedEditLead] = useState(null);
    const [showEntityEdit, setShowEntityEdit] = useState(false);
    const [viewingLeadId, setViewingLeadId] = useState(null);
    const [initialMode, setInitialMode] = useState('view');

    // Scorecard Hover Popup State
    const [hoveredLeadId, setHoveredLeadId] = useState(null);
    const [scorecardData, setScorecardData] = useState(null);
    const [isScorecardLoading, setIsScorecardLoading] = useState(false);
    const [popupPosition, setPopupPosition] = useState({ top: 0, left: 0 });
    const scorecardTimerRef = useRef(null);
    const statsInitializedRef = useRef(false); // Guard to prevent multiple calls
    const [sidebarFilter, setSidebarFilter] = useState('All');

    const [activeFilters, setActiveFilters] = useState(['All']);

    // Initialize activeFilters from dashboardFilter on mount
    useEffect(() => {
        if (dashboardFilter) {
            let newActiveFilters = [];
            if (dashboardFilter.Overdue) newActiveFilters.push('Overdue');
            if (dashboardFilter.DueToday) newActiveFilters.push('DueToday');
            if (dashboardFilter.Scheduled) newActiveFilters.push('Scheduled');
            if (dashboardFilter.NoFollowup) newActiveFilters.push('NoFollowUp');
            if (dashboardFilter.DueThisMonth) newActiveFilters.push('DueThisMonth');

            if (newActiveFilters.length > 0) {
                setActiveFilters(newActiveFilters);
            }
        }
    }, [dashboardFilter]);
    const [unfilteredStats, setUnfilteredStats] = useState({
        total: 0, dueToday: 0, overdue: 0, scheduled: 0, noFollowUp: 0
    });
    const [isStatsLoading, setIsStatsLoading] = useState(true);
    const [filters, setFilters] = useState({
        orderBy: 'Date (Oldest First)',
        filterByDate: 'All',
        status: 'Status',
        outcomes: 'Outcomes',
        advancedSearch: {
            logic: 'All',
            conditions: [{ field: 'Field', operator: 'Contains', value: '' }]
        },
        Overdue: true,
        DueToday: true,
        Scheduled: true,
        NoFollowUp: true,
        NotCompleted: false,
        Open: false,
        FollowupStatus: '',
        FollowUpType: '',
        AssignedTo: '',
        CreatedBy: '',
        NextStatusFromDate: '',
        NextStatusToDate: '',
        CreatedFromDate: '',
        CreatedToDate: '',
        LeadId: '',
        Remarks: ''
    });

    // Performance Constants & Refs
    const CHUNK_CACHE_KEY = 'FOLLOWUP_CHUNK_CACHE';
    const abortControllerRef = useRef(null);
    const lastRequestRef = useRef(null);

    const [searchTerm, setSearchTerm] = useState('');

    const [isCustomDownloadOpen, setIsCustomDownloadOpen] = useState(false);
    const [customExportFields, setCustomExportFields] = useState([]);
    const [customSettings, setCustomSettings] = useState([]);
    const [selectedExportFields, setSelectedExportFields] = useState([]);
    const [selectedCustomSetting, setSelectedCustomSetting] = useState('');
    const [exportSettingName, setExportSettingName] = useState('');
    const [isDownloading, setIsDownloading] = useState(false);
    const [fieldSearchTerm, setFieldSearchTerm] = useState('');
    const [showCallWidget, setShowCallWidget] = useState(false);
    const [callingLead, setCallingLead] = useState(null);
    const [callTriggerRect, setCallTriggerRect] = useState(null);
    const [interactionHistoryData, setInteractionHistoryData] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [historyLead, setHistoryLead] = useState(null);
    const [advancedFilters, setAdvancedFilters] = useState(null);

    const followUpFields = [
        { label: 'Related To (Lead)', value: 'PersonName' },
        { label: 'Follow-up Status', value: 'FollowupStatus' },
        { label: 'Subject/Remarks', value: 'Remarks' },
        { label: 'Agent', value: 'AgentName' },
        { label: 'Next Follow-up Date', value: 'FollowupDate', type: 'date' },
        { label: 'Created On', value: 'CreatedOn', type: 'date' },
        { label: 'Lead ID', value: 'LeadId' }
    ];


    const API_BASE_AZURE = (process.env.REACT_APP_SERVICES_AZURE_BASEURL || '').replace(/\/$/, '');
    const API_BASE = (process.env.REACT_APP_WCF_API_BASE_URL || '').replace(/\/$/, '');









    
    const NEWV3_BASE_URL = (process.env.REACT_APP_SERVICES_API_BASE_URL || '').replace(/\/$/, '');

    const GET_INTERACTION_HISTORY = `${NEWV3_BASE_URL}${API_ENDPOINTS.APPOINTMENTS.GET_INTERACTION_HISTORY}`;
    const WCF_BASE = (process.env.REACT_APP_WCF_API_BASE_URL || '').replace(/\/$/, '');
    const API_SERVICE_BASE = (process.env.REACT_APP_SERVICES_AZURE_BASEURL || '').replace(/\/$/, '');
    const SCORECARD_URL = `${NEWV3_BASE_URL}${API_ENDPOINTS.FOLLOWUPS.SCORE_CARD_DETAILS}`;
    const GetFollowUp_List = `${API_SERVICE_BASE}${API_ENDPOINTS.FOLLOWUPS.GET_LIST}`;
    const GET_INTERACTION_HISTORY_NEW = `${NEWV3_BASE_URL}${API_ENDPOINTS.APPOINTMENTS.GET_INTERACTION_HISTORY}`;
    const ADD_LEAD_FOLLOWUP = `${NEWV3_BASE_URL}${API_ENDPOINTS.APPOINTMENTS.ADD_LEAD_FOLLOWUP}`;
    const Export_FollowUp = `${NEWV3_BASE_URL}${API_ENDPOINTS.FOLLOWUPS.FOLLOWUP_EXPORT_NEW}`;
    const FollowUp_for_Redirect = `${WCF_BASE}${API_ENDPOINTS.FOLLOWUPS.FOLLOWUP_FOR_REDIRECT}`;
    
    const handleCallClick = useCallback((e, followup) => {
        e.stopPropagation();
        const rect = e.currentTarget.getBoundingClientRect();
        setCallTriggerRect(rect);
        setCallingLead(followup);
        setShowCallWidget(true);
    }, []);

    const fetchScorecardData = useCallback(async (leadId) => {
        if (!leadId) return;
        setIsScorecardLoading(true);
        try {
            const response = await fetch(SCORECARD_URL, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ Token: "-2295521862261168", Details: { EnquiryID: leadId.toString() } })
            });
            const scData = await response.json();
            const details = scData?.Details?.[0] || scData?.d?.[0] || scData?.Details || scData?.d;
            if (details) {
                let parsed = typeof details === 'string' ? JSON.parse(details) : details;
                if (Array.isArray(parsed)) parsed = parsed[0];
                setScorecardData(parsed);
            } else {
                setScorecardData(null);
            }
        } catch (e) {
            console.error('Error fetching scorecard data:', e);
            setScorecardData(null);
        } finally {
            setIsScorecardLoading(false);
        }
    }, [SCORECARD_URL]);

    const handleScorecardMouseEnter = useCallback((e, leadId) => {
        if (!leadId || leadId === "0") return;
        const rect = e.currentTarget.getBoundingClientRect();
        setPopupPosition({
            top: rect.top + window.scrollY - 10,
            left: rect.left + window.scrollX + rect.width / 2
        });

        if (scorecardTimerRef.current) clearTimeout(scorecardTimerRef.current);
        scorecardTimerRef.current = setTimeout(() => {
            setHoveredLeadId(leadId);
            fetchScorecardData(leadId);
        }, 400); // Debounce hover
    }, [fetchScorecardData]);

    const handleScorecardMouseLeave = useCallback(() => {
        if (scorecardTimerRef.current) clearTimeout(scorecardTimerRef.current);
        setHoveredLeadId(null);
        setScorecardData(null);
    }, []);

    const fetchInteractionHistory = useCallback(async (fu) => {
        setHistoryLead(fu);
        setHistoryLoading(true);
        try {
            const resp = await fetch(GET_INTERACTION_HISTORY_NEW, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    Token: "-2295521862261168",
                    Details: {
                        LeadNo: parseInt(fu.LeadId || fu.leadId || fu.enquiryId || 0, 10),
                        ParentID: parseInt(localStorage.getItem('UserId') || '34594', 10)
                    }
                })
            });
            const result = await resp.json();
            const data = result.d || result;
            setInteractionHistoryData(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error('Error fetching interaction history:', error);
            setInteractionHistoryData([]);
        } finally {
            setHistoryLoading(false);
        }
    }, [GET_INTERACTION_HISTORY_NEW]);

    // Fetch history when grid item is selected
    useEffect(() => {
        if (selectedGridFollowUp) {
            fetchInteractionHistory(selectedGridFollowUp);
        }
    }, [selectedGridFollowUp, fetchInteractionHistory]);

    const handleAction = useCallback((e, followup, mode) => {
        e.stopPropagation();

        if (mode === 'call') {
            handleCallClick(e, followup);
            return;
        }

        setSelectedFollowUp(followup);
        setInitialMode(mode);
        setShowDetails(true);
    }, [handleCallClick]);


    const formatLeadDate = (dateStr) => {
        if (!dateStr) return 'N/A';
        try {
            const date = new Date(dateStr);
            if (isNaN(date.getTime())) return dateStr;
            return date.toLocaleDateString('en-GB', {
                day: '2-digit',
                month: 'short',
                year: 'numeric'
            });
        } catch (e) {
            return dateStr;
        }
    };

    const formatDateTime = (dateStr) => {
        if (!dateStr) return '';
        const d = new Date(dateStr);
        if (isNaN(d.getTime())) return dateStr;
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const pad = (n) => n.toString().padStart(2, '0');
        return `${pad(d.getDate())}-${months[d.getMonth()]}-${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
    };

    const parseResponse = async (resp) => {
        try {
            const text = await resp.text();
            // Fast check for XML wrap
            if (text.startsWith('<?xml') || text.includes('<string')) {
                const xmlMatch = text.match(/<string[^>]*>([\s\S]*?)<\/string>/);
                if (xmlMatch) return JSON.parse(xmlMatch[1]);
            }
            return JSON.parse(text);
        } catch (e) {
            console.error('parseResponse error:', e);
            return null;
        }
    };

    const extractPayload = (res) => {
        if (!res) return res;
        if (res.data !== undefined) return res.data;
        if (res.d !== undefined) {
            if (typeof res.d === 'string') {
                try {
                    return JSON.parse(res.d);
                } catch {
                    return res.d;
                }
            }
            return res.d;
        }
        return res;
    };

    // Helper: Parse custom date string "31-Oct-2025 10:11:14"
    const parseCustomDate = (dateStr) => {
        if (!dateStr || dateStr === "0") return null;
        try {
            if (/^\d{4}-\d{2}-\d{2}/.test(dateStr)) {
                return new Date(dateStr);
            }
            const months = { 'Jan': 0, 'Feb': 1, 'Mar': 2, 'Apr': 3, 'May': 4, 'Jun': 5, 'Jul': 6, 'Aug': 7, 'Sep': 8, 'Oct': 9, 'Nov': 10, 'Dec': 11 };
            const parts = dateStr.trim().split(' ');
            if (parts.length < 1 || !parts[0]) return null;
            const dateParts = parts[0].split('-');
            if (dateParts.length < 3) return new Date(dateStr);
            const day = parseInt(dateParts[0], 10);
            const month = months[dateParts[1]];
            const year = parseInt(dateParts[2], 10);
            if (isNaN(day) || month === undefined || isNaN(year)) return null;
            let hours = 0, minutes = 0, seconds = 0;
            if (parts[1]) {
                const timeParts = parts[1].split(':');
                hours = parseInt(timeParts[0], 10) || 0;
                minutes = parseInt(timeParts[1], 10) || 0;
                seconds = parseInt(timeParts[2], 10) || 0;
            }
            return new Date(year, month, day, hours, minutes, seconds);
        } catch (e) {
            console.error("Date parsing error:", e);
            return null;
        }
    };

    // Helper: Calculate days difference for badges
    const getDaysDiff = (nextDate) => {
        if (!nextDate) return "";
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const next = new Date(nextDate);
        next.setHours(0, 0, 0, 0);
        const diffTime = next - today;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        if (diffDays < 0) return `Overdue ${Math.abs(diffDays)} Day(S)`;
        if (diffDays === 0) return "Due Today";
        return `Due In ${diffDays} Day(S)`;
    };

    const isToday = (dateStr) => {
        const date = parseCustomDate(dateStr);
        if (!date) return false;
        const now = new Date();
        const isSameDay = date.getDate() === now.getDate() &&
            date.getMonth() === now.getMonth() &&
            date.getFullYear() === now.getFullYear();

        return isSameDay;
    };

    const isOverdueFromDate = (dateStr) => {
        const date = parseCustomDate(dateStr);
        if (!date) return false;

        const now = new Date();
        const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        // Overdue ONLY if it's BEFORE today
        return date < startOfToday;
    };

    const [chunkSize] = useState(50); // reduced from 100 for faster load


    // Build JSON payload for new API
    const buildDataTablesPayload = () => {
        const userId = localStorage.getItem('userId') || '34594';
        const executiveLogin = dashboardFilter && dashboardFilter.Executive ? dashboardFilter.Executive : "";

        // Check activeFilters from scorecards
        const isAll = activeFilters.includes('All');
        const hasOverdue = activeFilters.includes('Overdue') || (dashboardFilter?.Overdue);
        const hasDueToday = activeFilters.includes('DueToday') || (dashboardFilter?.DueToday);
        const hasScheduled = activeFilters.includes('Scheduled') || (dashboardFilter?.Scheduled);
        const hasNoFollowUp = activeFilters.includes('NoFollowUp') || (dashboardFilter?.NoFollowup);
        const hasDueThisMonth = activeFilters.includes('DueThisMonth') || (dashboardFilter?.DueThisMonth);

        // Calendar needs more records to populate the full year view; table/grid use a smaller page
        let pagingSize = viewMode === 'calendar' ? 2000 : chunkSize;

        let offset = 0;
        if (viewMode !== 'calendar') {
            const currentChunk = Math.floor((currentPage - 1) / (chunkSize / itemsPerPage));
            offset = currentChunk * chunkSize;
        }

        // Build SQL-style filter text for the details object
        let filterText = "";

        // Status specific filtering from scorecards
        if (!isAll || (dashboardFilter && (dashboardFilter.Overdue || dashboardFilter.DueToday || dashboardFilter.Scheduled || dashboardFilter.NoFollowup || dashboardFilter.DueThisMonth))) {
            let statusParts = [];
            
            // EXACT user payloads
            if (hasOverdue) statusParts.push("CONVERT(date, FollowupDate) < CONVERT(date, GETDATE()) AND (FollowupStatus <> 'Completed' OR FollowupStatus IS NULL)"); 
            if (hasDueToday) statusParts.push("CONVERT(date, FollowupDate) = CONVERT(date, GETDATE())");
            
            if (hasScheduled) {
                if (dashboardFilter?.NextStatusFromDate && dashboardFilter?.NextStatusToDate) {
                    // Due in 7 Days exact payload
                    statusParts.push(`((CONVERT(date, FollowupDate) = CONVERT(date, GETDATE())) OR FollowupDate > GETDATE()) AND FollowupDate >= '${dashboardFilter.NextStatusFromDate}' AND FollowupDate <= '${dashboardFilter.NextStatusToDate} 23:59:59'`);
                } else {
                    statusParts.push("FollowupDate > GETDATE()");
                }
            }
            
            if (hasDueThisMonth) {
                statusParts.push("MONTH(FollowupDate) = MONTH(GETDATE()) AND YEAR(FollowupDate) = YEAR(GETDATE())");
            }
            
            if (hasNoFollowUp) statusParts.push("FollowupDate IS NULL");

            if (statusParts.length > 0) {
                filterText += ` AND (${statusParts.join(' OR ')})`;
            }
        }

        // Date filters from advanced filter bar (ignore dashboardFilter ones here to avoid double-appending for 7 Days)
        const fromDate = filters.NextStatusFromDate || '';
        const toDate = filters.NextStatusToDate || '';
        if (fromDate) filterText += ` AND FollowupDate >= '${fromDate}'`;
        if (toDate) filterText += ` AND FollowupDate <= '${toDate} 23:59:59'`;

        const cFromDate = filters.CreatedFromDate || '';
        const cToDate = filters.CreatedToDate || '';
        if (cFromDate) filterText += ` AND CreatedOn >= '${cFromDate}'`;
        if (cToDate) filterText += ` AND CreatedOn <= '${cToDate} 23:59:59'`;

        // Discrete filters
        if (filters.FollowUpType) filterText += ` AND FollowUpType = '${filters.FollowUpType}'`;
        if (filters.AssignedTo) filterText += ` AND AssignedTo = ${filters.AssignedTo}`;
        if (filters.CreatedBy) filterText += ` AND CreatedBy = ${filters.CreatedBy}`;
        if (filters.LeadId) filterText += ` AND LeadId = ${filters.LeadId}`;
        if (filters.Remarks) {
            const fuzzyRemarks = filters.Remarks.trim().replace(/'/g, "''").replace(/\s+/g, '%');
            filterText += ` AND (Remarks LIKE '%${fuzzyRemarks}%' OR PersonName LIKE '%${fuzzyRemarks}%')`;
        }
        if (filters.outcomes && filters.outcomes !== 'Outcomes' && filters.outcomes !== '') {
            filterText += ` AND Outcome = '${filters.outcomes}'`;
        }

        // Advanced Filters logic
        let hasAllChecked = false;
        if (advancedFilters) {
            const { logic, rows, allChecked } = advancedFilters;
            hasAllChecked = allChecked;

            if (rows && rows.length > 0) {
                const sqlRows = rows.map(row => {
                    const val = String(row.value || '').replace(/'/g, "''");
                    const fieldInfo = followUpFields.find(f => f.value === row.field);
                    const isDate = fieldInfo?.type === 'date';

                    if (isDate) {
                        // For date fields, we compare only the date part to ensure match
                        return `CAST(${row.field} AS DATE) = '${val}'`;
                    }

                    switch (row.operator) {
                        case 'equals': return `${row.field} = '${val}'`;
                        case 'contains': return `${row.field} LIKE '%${val.replace(/\s+/g, '%')}%'`;
                        case 'not_contains': return `${row.field} NOT LIKE '%${val.replace(/\s+/g, '%')}%'`;
                        case 'not_equal': return `${row.field} <> '${val}'`;
                        case 'starts_with': return `${row.field} LIKE '${val}%'`;
                        case 'ends_with': return `${row.field} LIKE '%${val}'`;
                        default: return `${row.field} LIKE '%${val}%'`;
                    }
                }).filter(Boolean);

                if (sqlRows.length > 0) {
                    const joined = sqlRows.join(logic === 'ALL' ? ' AND ' : ' OR ');
                    filterText += ` AND (${joined})`;
                }
            }
        }

        // Completion status - Skip filtering out Completed if 'All Follow Up' (hasAllChecked) is true
        if (!hasAllChecked) {
            if (filters.FollowupStatus && filters.FollowupStatus !== 'Status' && filters.FollowupStatus !== 'All') {
                filterText += ` AND FollowupStatus = '${filters.FollowupStatus}'`;
            } else if (filters.NotCompleted || filters.Open) {
                filterText += " AND (FollowupStatus <> 'Completed' OR FollowupStatus IS NULL)";
            }
        }

        // Global Search Term fallback in FilterText
        if (searchTerm && searchTerm.trim()) {
            // Normalize: remove all spaces for comparison
            const rawSearch = searchTerm.trim();
            const normalizedSearch = rawSearch.replace(/\s+/g, '').replace(/'/g, "''");
            const fuzzySearch = rawSearch.replace(/'/g, "''").replace(/\s+/g, '%');

            // Build phone search variants: strip leading country codes (91, +91, 0) for Indian numbers
            const phoneVariants = [normalizedSearch];
            if (/^(\+?91|0)\d{10}$/.test(normalizedSearch)) {
                // e.g. 919123456789 -> 9123456789
                phoneVariants.push(normalizedSearch.replace(/^(\+?91|0)/, ''));
            } else if (/^\d{10}$/.test(normalizedSearch)) {
                // 10-digit number -> also try with 91 prefix
                phoneVariants.push('91' + normalizedSearch);
            }
            const phoneConditions = phoneVariants.map(p => `MobileNo LIKE '%${p}%'`).join(' OR ');

            filterText += ` AND (PersonName LIKE '%${fuzzySearch}%' OR Remarks LIKE '%${fuzzySearch}%' OR (${phoneConditions}) OR CAST(LeadId AS VARCHAR) LIKE '%${normalizedSearch}%')`;
        }

        // Remove leading " AND " if it exists
        if (filterText.trim().startsWith("AND ")) {
            filterText = filterText.trim().substring(4);
        }

        const queryParams = new URLSearchParams(location.search);
        let extraParams = {};
        for (let [key, value] of queryParams.entries()) {
            extraParams[key] = value;
        }

        return {
            Token: "-2295521862261168",
            ...extraParams,
            Details: {
                UserId: parseInt(userId),
                ...(executiveLogin ? { cUserLogin: executiveLogin } : {}),
                FullText: searchTerm || "",
                FilterText: filterText,
                SearchText: searchTerm || "",
                OffsetValue: offset,
                PagingSize: pagingSize,
                OrderBy: filters.orderBy || "",
                CustomSearchId: customSearchId || 0,
                ...extraParams
            }
        };
    };

    // Pre-calculate status for each follow-up to optimize rendering
    const followUpsWithStatus = useMemo(() => {
        return followUps.map(fu => {
            // Map new API fields to internal component fields
            const mappedFu = {
                ...fu,
                ID: fu.Id || fu.ID || fu.id,
                Title: fu.Title || fu.FollowupStatus || fu.followupstatus || fu.Remarks || fu.remarks || '-',
                NextStatusDate: fu.FollowupDate || fu.followupdate || fu.NextStatusDate,
                MobileNo: fu.CsvMobileNo || fu.MobileNo || fu.mobileNo || '-',
                Owner: fu.AgentName || fu.agentname || fu.OwnerName || fu.Owner || '-',
                PersonName: fu.PersonName || fu.personName || fu.personname || fu.FullName || fu.name || '-',
                LeadId: fu.LeadId || fu.leadid || fu.enquiryId || '-',
                CreatedOn: fu.CreatedOn || fu.createdon || '-'
            };

            const overdue = isOverdueFromDate(mappedFu.NextStatusDate);
            const today = isToday(mappedFu.NextStatusDate);
            let statusClass = today ? 'due-today' : (overdue ? 'overdue' : (mappedFu.NextStatusDate ? 'scheduled' : 'no-followup'));
            let badgeText = mappedFu.NextStatusDate ? getDaysDiff(mappedFu.NextStatusDate) : null;

            if (fu.IsConvert || fu.isConvert) {
                statusClass = 'converted';
                badgeText = null;
            }

            // For sub-component compatibility (FollowUpListWrapper)
            const wrapperFields = {
                taskTitle: mappedFu.Title,
                startDate: mappedFu.NextStatusDate,
                phone: mappedFu.MobileNo,
                name: mappedFu.PersonName,
                creatorName: mappedFu.Owner,
                creatorAvatar: fu.aImage, // Add agent avatar mapping
                leadId: mappedFu.LeadId, // lowercase leadId for ScoreCard and other sub-components
                taskStatus: statusClass,
                status: statusClass,
                statusLabel: (statusClass || '').toLowerCase(),
                endDate: mappedFu.NextStatusDate,
                completedDate: fu.CompletedDate || "",
                outcomeName: fu.Outcome || fu.Discription || "",
                createdDate: mappedFu.CreatedOn,
                conversionAmount: fu.AmountPaid || fu.Amount || fu.amount || "",
                convertedOn: fu.ConvertedOn || fu.convertedOn || fu.ConvertedDate || (fu.IsConvert ? fu.NextStatusDate : "")
            };

            return { ...mappedFu, ...wrapperFields, _overdue: overdue, _today: today, _statusClass: statusClass, _badgeText: badgeText };
        });
    }, [followUps]); // Only re-calculate when the raw followUps array changes

    // Helper: normalize a phone string by stripping spaces and common country code prefixes
    const normalizePhone = (phone) => {
        if (!phone) return '';
        // Remove all spaces, dashes, dots, brackets
        const stripped = String(phone).replace(/[\s\-\.()]/g, '');
        // Strip leading +91 / 91 / 0 to get the 10-digit core
        return stripped.replace(/^(\+?91|0)(?=\d{10}$)/, '');
    };

    // Memoized filtered follow-ups (Apply filters to the entire chunk before pagination)
    const filteredFollowUps = useMemo(() => {
        let results = [...followUpsWithStatus];

        // 1. Apply search filtering
        if (searchTerm.trim()) {
            // Normalize the full search term: remove spaces for phone matching
            const rawTerm = searchTerm.trim();
            const normalizedTerm = rawTerm.replace(/[\s\-\.()]/g, '').toLowerCase();
            const normalizedCorePhone = normalizePhone(normalizedTerm); // strip country code

            // Split into individual words for text fields (name, remarks, etc.)
            const searchWords = rawTerm.toLowerCase().trim().split(/\s+/).filter(Boolean);

            results = results.filter(fu => {
                // --- Phone number matching (space-insensitive, country-code-insensitive) ---
                const rawPhone = fu.MobileNo || fu.mobileNo || fu.CsvMobileNo || fu.phone || '';
                const storedPhone = String(rawPhone).replace(/[\s\-\.()]/g, '').toLowerCase();
                const storedCorePhone = normalizePhone(storedPhone);

                const phoneMatch =
                    storedPhone.includes(normalizedTerm) ||
                    storedPhone.includes(normalizedCorePhone) ||
                    storedCorePhone.includes(normalizedTerm) ||
                    storedCorePhone.includes(normalizedCorePhone) ||
                    (normalizedTerm && storedPhone.includes(normalizedTerm.replace(/^(\+?91|0)/, ''))) ||
                    (normalizedCorePhone && storedCorePhone.includes(normalizedCorePhone));

                // Allow any non-empty search to match phone numbers (e.g. "89" matches "8909888888")
                if (phoneMatch && normalizedTerm.length >= 1) return true;

                // --- Text field matching ---
                const searchableText = [
                    fu.PersonName,
                    fu.personname,
                    fu.personName,
                    fu.name,
                    fu.FullName,
                    fu.fullName,
                    fu.LeadName,
                    fu.Lead_Name,
                    fu.CompanyName,
                    fu.companyName,
                    fu.FollowUp,
                    fu.Title,
                    fu.Subject,
                    fu.Remarks,
                    fu.remarks,
                    fu.CsvMobileNo,
                    fu.MobileNo,
                    fu.mobileNo,
                    fu.phone,
                    fu.LeadId,
                    fu.leadId,
                    fu.LeadNo,
                    fu.AssignedTo,
                    fu.Owner,
                    fu.OwnerName,
                    fu.AgentName,
                    fu.agentname,
                    fu.creatorName,
                    fu.FollowupStatus,
                    fu.statusLabel
                ].filter(v => v !== undefined && v !== null && v !== '-')
                    .map(v => String(v).toLowerCase())
                    .join(' ');

                // Every search word must appear as a substring in the combined text
                return searchWords.every(word => searchableText.includes(word));
            });
        }

        // 2. Apply dynamic advanced filtering
        if (advancedFilters && advancedFilters.rows && Array.isArray(advancedFilters.rows)) {
            const { logic, rows } = advancedFilters;
            if (rows.length > 0) {
                results = results.filter(fu => {
                    const rowMatches = rows.map(row => {
                        const searchVal = String(row.value || '').toLowerCase().trim();
                        if (!searchVal) return true; // Empty search matches everything

                        // Collect all values to check (support multiple fields for some labels)
                        let valuesToCheck = [];

                        // Default value based on the selected field
                        const primaryVal = fu[row.field];
                        if (primaryVal !== undefined && primaryVal !== null) {
                            valuesToCheck.push(String(primaryVal).toLowerCase().trim());
                        }

                        // Robust fallback checking for common fields
                        if (row.field === 'PersonName') {
                            if (fu.name) valuesToCheck.push(String(fu.name).toLowerCase().trim());
                            if (fu.LeadName) valuesToCheck.push(String(fu.LeadName).toLowerCase().trim());
                            if (fu.FullName) valuesToCheck.push(String(fu.FullName).toLowerCase().trim());
                            if (fu.Lead_Name) valuesToCheck.push(String(fu.Lead_Name).toLowerCase().trim());
                        } else if (row.field === 'FollowupStatus') {
                            if (fu.Status) valuesToCheck.push(String(fu.Status).toLowerCase().trim());
                            if (fu.statusLabel) valuesToCheck.push(String(fu.statusLabel).toLowerCase().trim());
                        } else if (row.field === 'Remarks') {
                            if (fu.Title) valuesToCheck.push(String(fu.Title).toLowerCase().trim());
                            if (fu.FollowUp) valuesToCheck.push(String(fu.FollowUp).toLowerCase().trim());
                        } else if (row.field === 'AgentName') {
                            if (fu.Owner) valuesToCheck.push(String(fu.Owner).toLowerCase().trim());
                            if (fu.OwnerName) valuesToCheck.push(String(fu.OwnerName).toLowerCase().trim());
                            if (fu.creatorName) valuesToCheck.push(String(fu.creatorName).toLowerCase().trim());
                        }

                        // If no values found at all, treat as empty string
                        if (valuesToCheck.length === 0) valuesToCheck.push('');

                        // Check if ANY of the potential fields match the operator
                        return valuesToCheck.some(val => {
                            switch (row.operator) {
                                case 'equals': return val === searchVal;
                                case 'not_equal': return val !== searchVal;
                                case 'starts_with': return val.startsWith(searchVal);
                                case 'ends_with': return val.endsWith(searchVal);
                                case 'contains': return val.includes(searchVal);
                                case 'not_contains': return !val.includes(searchVal);
                                default: return val.includes(searchVal);
                            }
                        });
                    });

                    return logic === 'ALL' ? rowMatches.every(m => m) : rowMatches.some(m => m);
                });
            }
        }

        return results;
    }, [followUpsWithStatus, searchTerm, advancedFilters]);

    const displayedFollowUps = useMemo(() => {
        if (viewMode === 'calendar') return filteredFollowUps;

        // Calculate relative index within the current chunk of filtered results
        const startIndex = (currentPage - 1) % (chunkSize / itemsPerPage) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;

        return filteredFollowUps.slice(startIndex, endIndex);
    }, [filteredFollowUps, viewMode, currentPage, itemsPerPage, chunkSize]);

    const sidebarFilteredResults = useMemo(() => {
        return filteredFollowUps;
    }, [filteredFollowUps]);

    // Update totalPages to reflect filtered count if filters are active
    const effectiveTotalCount = (searchTerm.trim() || (advancedFilters && advancedFilters.rows && advancedFilters.rows.length > 0))
        ? filteredFollowUps.length
        : totalRecords;

    const totalPages = Math.ceil(effectiveTotalCount / itemsPerPage);

    // Calculate Global Stats for Scorecards
    const stats = useMemo(() => {
        // Use unfilteredStats once it has been set (total > 0 means fetchFullStats completed)
        if (unfilteredStats && unfilteredStats.total > 0) return unfilteredStats;

        // Fallback: while waiting for fetchFullStats, show totalRecords from main query
        return {
            total: totalRecords || 0,
            dueToday: unfilteredStats.dueToday || 0,
            overdue: unfilteredStats.overdue || 0,
            scheduled: unfilteredStats.scheduled || 0,
            noFollowUp: unfilteredStats.noFollowUp || 0
        };
    }, [followUpsWithStatus, totalRecords, unfilteredStats]);

    // Fetcher for React Query
    const followUpFetcher = async () => {
        const queryParams = new URLSearchParams(location.search);
        const isUserConversions = queryParams.get("Report") === "UserConversions";
        const executiveFilter = queryParams.get("Executive");
        const orderFromDate = queryParams.get("OrderFromDate") || "";
        const orderToDate = queryParams.get("OrderToDate") || "";
        const createdBy = queryParams.get("CreatedBy") || "";
        const assignedTo = queryParams.get("AssignedTo") || "";
        
        let endpoint = GetFollowUp_List;
        let payload;

        let fetchOptions = {};

        if (isUserConversions || executiveFilter) {
            endpoint = FollowUp_for_Redirect;
            const { userId } = getSession();

            const formatToMMM = (dateStr) => {
                if(!dateStr) return "";
                const parts = dateStr.split('/');
                if(parts.length === 3) {
                    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                    return `${parts[1].padStart(2, '0')}-${months[parseInt(parts[0], 10)-1]}-${parts[2]}`;
                }
                return dateStr;
            };

            let execAssignedTo = assignedTo;

            if (executiveFilter) {
                try {
                    const userList = await getUserListByUserId({
                        Token: "-2295521862261168",
                        Details: { UserId: parseInt(userId || "34594", 10) }
                    });
                    if (userList && userList.length > 0) {
                        const matchedUser = userList.find(u => u.Text && u.Text.includes(executiveFilter));
                        if (matchedUser) {
                            execAssignedTo = matchedUser.Code;
                        }
                    }
                } catch (err) {
                    console.error("Error fetching user list for executive filter:", err);
                }
            } else {
                execAssignedTo = queryParams.get("AssignedTo") || assignedTo;
            }

            const execFromDate = executiveFilter ? queryParams.get("FromDate") : "";
            const execToDate = executiveFilter ? queryParams.get("ToDate") : "";
            const execOverdue = executiveFilter ? queryParams.get("OverDue") === "true" : true;
            const execDueToday = executiveFilter ? queryParams.get("DueToday") === "true" : true;
            const execScheduled = executiveFilter ? queryParams.get("Scheduled") === "true" : true;
            const execNoFollowUp = executiveFilter ? queryParams.get("NoFollowup") === "true" : true;

            payload = {
                "draw": 1,
                "columns[0][data]": "",
                "columns[0][name]": "",
                "columns[0][searchable]": true,
                "columns[0][orderable]": false,
                "columns[0][search][value]": "",
                "columns[0][search][regex]": false,
                "columns[1][data]": "",
                "columns[1][name]": "",
                "columns[1][searchable]": true,
                "columns[1][orderable]": false,
                "columns[1][search][value]": "",
                "columns[1][search][regex]": false,
                "columns[2][data]": "",
                "columns[2][name]": "",
                "columns[2][searchable]": true,
                "columns[2][orderable]": false,
                "columns[2][search][value]": "",
                "columns[2][search][regex]": false,
                "columns[3][data]": "",
                "columns[3][name]": "",
                "columns[3][searchable]": true,
                "columns[3][orderable]": false,
                "columns[3][search][value]": "",
                "columns[3][search][regex]": false,
                "columns[4][data]": "",
                "columns[4][name]": "",
                "columns[4][searchable]": true,
                "columns[4][orderable]": false,
                "columns[4][search][value]": "",
                "columns[4][search][regex]": false,
                "columns[5][data]": "",
                "columns[5][name]": "",
                "columns[5][searchable]": true,
                "columns[5][orderable]": false,
                "columns[5][search][value]": "",
                "columns[5][search][regex]": false,
                "order[0][column]": 0,
                "order[0][dir]": "asc",
                "start": (currentPage - 1) * itemsPerPage,
                "length": itemsPerPage,
                "search[value]": searchTerm || "",
                "search[regex]": false,
                "UserId": userId || "34594",
                "FollowUpType": isUserConversions ? "112" : "",
                "AssignedTo": execAssignedTo || "",
                "CreatedBy": createdBy,
                "NextStatusFromDate": formatToMMM(execFromDate),
                "NextStatusToDate": formatToMMM(execToDate),
                "CreatedFromDate": orderFromDate,
                "CreatedToDate": orderToDate,
                "LeadId": "",
                "Remarks": "",
                "Overdue": execOverdue,
                "DueToday": execDueToday,
                "Scheduled": execScheduled,
                "NoFollowUp": execNoFollowUp,
                "IsReAssign": "",
                "CriteriaName": "",
                "TimeZone": "India Standard Time"
            };
            
            const formBody = new URLSearchParams();
            for (const key in payload) {
                formBody.append(key, payload[key]);
            }
            
            fetchOptions = {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
                    'Accept': 'application/json'
                },
                body: formBody.toString()
            };
        } else {
            payload = buildDataTablesPayload();
            fetchOptions = {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json; charset=UTF-8',
                    'Accept': 'application/json'
                },
                body: JSON.stringify(payload)
            };
        }

        console.log("=== API DEBUG: Firing FollowUp Fetcher ===");
        console.log("Endpoint:", endpoint);
        console.log("Is User Conversions Redirect?:", isUserConversions);
        console.log("Payload being sent:", payload);

        const response = await fetch(endpoint, fetchOptions);

        if (!response.ok) throw new Error('Network response was not ok');

        // Use the robust parseResponse helper to handle XML-wrapped JSON
        const result = await parseResponse(response);
        if (!result) throw new Error('Failed to parse response');

        // Extract the payload (data and total)
        const details = result.Details || result.d || result;
        const list = Array.isArray(details.data) ? details.data : (Array.isArray(details) ? details : []);
        const total = details.totalRecords !== undefined ? details.totalRecords : (details.total !== undefined ? details.total : list.length);

        return { list, total };
    };

    const {
        data: queryData,
        isLoading: isQueryLoading,
        isFetching,
        isError,
        refetch: onRefresh
    } = useQuery({
        // Include searchTerm in queryKey to trigger refetch on search
        queryKey: ['followUps', activeFilters, filters, advancedFilters, searchTerm, customSearchId, currentPage, viewMode, location.search],
        queryFn: followUpFetcher,
        staleTime: 5 * 60 * 1000,
        placeholderData: (previousData) => previousData
    });

    // Derive loading state from React Query
    const loading = isQueryLoading || isFetching;

    // Handle list data
    useEffect(() => {
        if (queryData) {
            setFollowUps(queryData.list);
            setTotalRecords(queryData.total);
            if (isInitialLoad) {
                setIsInitialLoad(false);
            }
        }
    }, [queryData]);

    // Reset selection on search to ensure details panel refreshes with the top result
    useEffect(() => {
        if (searchTerm) {
            setSelectedGridFollowUp(null);
        }
    }, [searchTerm]);

    // Automatically select the first follow-up when switching to grid view if none is selected
    useEffect(() => {
        if (viewMode === 'grid' && !selectedGridFollowUp && sidebarFilteredResults && sidebarFilteredResults.length > 0) {
            setSelectedGridFollowUp(sidebarFilteredResults[0]);
        }
    }, [viewMode, sidebarFilteredResults, selectedGridFollowUp]);

    // Fetch scorecard stats ONCE on mount. Never re-fetch on filter changes.
    useEffect(() => {
        if (!statsInitializedRef.current) {
            statsInitializedRef.current = true;
            fetchFullStats();
        }
    }, []); // Empty deps = runs once on mount only

    const fetchFullStats = async () => {
        try {
            const userId = localStorage.getItem('userId') || '34594';
            const executiveLogin = dashboardFilter && dashboardFilter.Executive ? dashboardFilter.Executive : "";
            
            const queryParams = new URLSearchParams(location.search);
            let extraParams = {};
            for (let [key, value] of queryParams.entries()) {
                extraParams[key] = value;
            }

            const basePayload = {
                Token: "-2295521862261168",
                ...extraParams,
                Details: {
                    UserId: parseInt(userId),
                    ...(executiveLogin ? { cUserLogin: executiveLogin } : {}),
                    FullText: "", SearchText: "", FilterText: "", OffsetValue: 0, PagingSize: 1, ...extraParams
                }
            };

            // NOTE: Only 3 queries. Overdue is SKIPPED (it timeouts on large datasets)
            // Overdue will be deduced mathematically: Overdue = main total - (Today + Scheduled + NoFollowup)
            const statusQueries = [
                { key: 'dueToday', filter: "(CONVERT(date, FollowupDate) = CONVERT(date, GETDATE())) AND (FollowupStatus <> 'Completed' OR FollowupStatus IS NULL)" },
                { key: 'scheduled', filter: "FollowupDate > GETDATE() AND (FollowupStatus <> 'Completed' OR FollowupStatus IS NULL)" },
                { key: 'noFollowUp', filter: "FollowupDate IS NULL AND (FollowupStatus <> 'Completed' OR FollowupStatus IS NULL)" }
            ];

            const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));
            const counts = { dueToday: 0, scheduled: 0, noFollowUp: 0 };

            // Step 1: Fetch total from main (no-filter) query for the global total
            // IMPORTANT: FilterText must be empty string, not missing, to avoid SP error
            let globalTotal = 0;
            try {
                const mainResp = await fetch(GetFollowUp_List, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json; charset=UTF-8', 'Accept': 'application/json' },
                    body: JSON.stringify({
                        Token: "-2295521862261168",
                        ...extraParams,
                        Details: { 
                            UserId: parseInt(userId), 
                            ...(executiveLogin ? { cUserLogin: executiveLogin } : {}),
                            FullText: "", SearchText: "", FilterText: "", OffsetValue: 0, PagingSize: 1, ...extraParams 
                        }
                    })
                });
                if (mainResp.ok) {
                    const mainRaw = await mainResp.json();
                    if (mainRaw.Status === 1 && mainRaw.Details) {
                        globalTotal = mainRaw.Details.totalRecords || 0;
                    }
                }
            } catch (e) {
            }

            // Immediately show total in UI
            setUnfilteredStats(prev => ({ ...prev, total: globalTotal }));

            // Step 2: Sequentially fetch 3 category counts
            for (const q of statusQueries) {
                try {
                    await delay(50);
                    const resp = await fetch(GetFollowUp_List, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json; charset=UTF-8', 'Accept': 'application/json' },
                        body: JSON.stringify({
                            ...basePayload,
                            Details: { ...basePayload.Details, FilterText: q.filter }
                        })
                    });
                    if (!resp.ok) continue;
                    const raw = await resp.json();
                    if (raw.Status === 1 && raw.Details) {
                        counts[q.key] = raw.Details.totalRecords || 0;
                    }
                } catch (err) {
                    console.error(`Stats fetch error for ${q.key}:`, err);
                }
            }

            // Step 3: Deduce Overdue = Total - (Today + Scheduled + NoFollowup)
            const deducedOverdue = Math.max(0, globalTotal - counts.dueToday - counts.scheduled - counts.noFollowUp);

            // Step 4: Single atomic state update — never updates again unless refresh
            setUnfilteredStats({
                total: globalTotal,
                dueToday: counts.dueToday,
                scheduled: counts.scheduled,
                noFollowUp: counts.noFollowUp,
                overdue: deducedOverdue
            });
            setIsStatsLoading(false);
        } catch (e) {
            console.error('Error in fetchFullStats:', e);
            setIsStatsLoading(false);
        }
    };


    // Cleanup redundant logic
    const fetchFollowUps = onRefresh;

    useEffect(() => {
        // Debounce search/filter changes to prevent excessive re-fetching
        const timer = setTimeout(() => {
            // This trigger is handled automatically by queryKey changing
        }, 300);
        return () => clearTimeout(timer);
    }, [filters, searchTerm, dashboardFilter]);

    const clearDashboardFilter = () => {
        setDashboardFilter(null);
        setFilterLabel(null);
        // Clean up navigation state
        navigate(location.pathname, { replace: true, state: {} });
    };

    // Automatically select the first follow-up in grid view when data is loaded
    // Fetch Custom Export data when modal opens
    useEffect(() => {
        if (isCustomDownloadOpen) {
            const fetchCustomExportData = async () => {
                const uid = localStorage.getItem('userId') || '34594';
                const token = API_ENDPOINTS.TOKE.UNIVERSAL_TOKEN.toString();

                // 1. Fetch Placeholders
                try {
                    const NEWV3_BASE_URL = (process.env.REACT_APP_SERVICES_API_BASE_URL || '').replace(/\/$/, '');
                    const phUrl = `${NEWV3_BASE_URL}/UserCRM/FuncToGetEntityPlaceHolder`;
                    const res = await fetch(phUrl, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ Token: token, Details: { strEntityName: "Lead", strParentID: uid } })
                    });
                    const result = await res.json();
                    if (result && result.Status === 1 && result.Details) {
                        setCustomExportFields(result.Details);
                    } else if (result && result.d) {
                        setCustomExportFields(result.d);
                    }
                } catch (e) {
                    console.error('Error fetching placeholders:', e);
                }

                // 2. Fetch Settings
                try {
                    const settingsUrl = `${API_BASE_AZURE}${API_ENDPOINTS.COMMON.EXPORT_DATA_SETTING}`;
                    const payload = {
                        Details: {
                            Id: 0,
                            SettingName: "",
                            PageUrl: "/UserCRMCampaign/ToDo/followup.aspx",
                            CsvColumns: "",
                            Section: "FollowUp",
                            EntityName: "Entity",
                            UserId: uid,
                            Mode: "get"
                        },
                        Token: token
                    };
                    const res = await fetch(settingsUrl, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(payload)
                    });
                    const result = await res.json();
                    if (result && result.Details && result.Details.data) {
                        setCustomSettings(result.Details.data);
                    } else if (result && result.data) {
                        setCustomSettings(result.data);
                    }
                } catch (e) {
                    console.error('Error fetching settings:', e);
                }
            };
            fetchCustomExportData();
        }
    }, [isCustomDownloadOpen]);

    const handleSaveExportSetting = async () => {
        let nameToSave = exportSettingName;
        let existingId = 0;

        if (!nameToSave && selectedCustomSetting) {
            const currentSetting = customSettings.find(s => s.Id.toString() === selectedCustomSetting);
            if (currentSetting) {
                nameToSave = currentSetting.SettingName;
                existingId = currentSetting.Id;
            }
        }

        if (!nameToSave || selectedExportFields.length === 0) {
            alert("Please enter a name for the setting and select at least one field.");
            return;
        }

        const uid = localStorage.getItem('userId') || '34594';
        try {
            const token = API_ENDPOINTS.TOKE.UNIVERSAL_TOKEN.toString();
            const payload = {
                Details: {
                    Id: existingId,
                    SettingName: nameToSave,
                    PageUrl: "/UserCRMCampaign/ToDo/followup.aspx",
                    CsvColumns: selectedExportFields.join(','),
                    Section: "FollowUp",
                    EntityName: "Entity",
                    UserId: uid,
                    Mode: "save"
                },
                Token: token
            };
            const res = await fetch('https://serviceskit19.azurewebsites.net/EmployeeTracker/ExportDataSetting', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            const result = await res.json();
            if (result.Status === 1) {
                alert("Setting saved successfully!");
                setExportSettingName('');
                // Refresh settings list
                const sPayload = {
                    Details: {
                        Id: 0,
                        SettingName: "",
                        PageUrl: "/UserCRMCampaign/ToDo/followup.aspx",
                        CsvColumns: "",
                        Section: "FollowUp",
                        EntityName: "Entity",
                        UserId: uid,
                        Mode: "get"
                    },
                    Token: token
                };
                const sRes = await fetch('https://serviceskit19.azurewebsites.net/EmployeeTracker/ExportDataSetting', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(sPayload)
                });
                const sResult = await sRes.json();
                if (sResult && sResult.Details && sResult.Details.data) setCustomSettings(sResult.Details.data);
            } else {
                alert("Failed to save: " + result.Message);
            }
        } catch (e) {
            console.error('Error saving setting:', e);
            alert("Error saving setting.");
        }
    };

    const handleCustomDownloadSubmit = async () => {
        if (selectedExportFields.length === 0) {
            alert("Select fields first.");
            return;
        }
        setIsDownloading(true);
        const uid = localStorage.getItem('userId') || '34594';
        try {
            const payload = {
                UserId: uid,
                EntityName: "Lead",
                PageMode: "FollowUp",
                Mode: "F",
                CriteriaName: searchTerm || "",
                FilterText: "",
                csvColumnList: selectedExportFields.join(','),
                PageSection: "FollowUp",
                FollowUpType: filters.FollowUpType || "AssignedTo",
                AssignedTo: filters.AssignedTo || "0",
                CreatedBy: filters.CreatedBy || "",
                NextStatusFromDate: filters.NextStatusFromDate || "",
                NextStatusToDate: filters.NextStatusToDate || "",
                CreatedFromDate: filters.CreatedFromDate || "",
                CreatedToDate: filters.CreatedToDate || "",
                LeadId: filters.LeadId || "0",
                Remarks: filters.Remarks || "",
                Overdue: (filters.Overdue ?? true).toString(),
                DueToday: (filters.DueToday ?? true).toString(),
                Scheduled: (filters.Scheduled ?? true).toString(),
                NoFollowUp: (filters.NoFollowUp ?? true).toString(),
                IsReAssign: filters.IsReAssign || "false"
            };


            // Use a hidden form to submit as x-www-form-urlencoded and handle the redirect/download
            // as shown in the user's Postman/browser examples.
            const form = document.createElement('form');
            form.method = 'POST';
            form.action = 'https://wcfkit19.azurewebsites.net/UserCRMCampaign/Service/CallReportService.asmx/ExportWidgetDownload';
            form.target = '_blank';

            Object.keys(payload).forEach(key => {
                const input = document.createElement('input');
                input.type = 'hidden';
                input.name = key;
                input.value = payload[key];
                form.appendChild(input);
            });

            document.body.appendChild(form);
            form.submit();
            document.body.removeChild(form);

            setIsCustomDownloadOpen(false);
        } catch (e) {
            console.error('Download error:', e);
            alert("Error during custom download.");
        } finally {
            setIsDownloading(false);
        }
    };

    // --- Server-side Export Handler (via API → CSV download from server) ---
    const handleExportTasks = async () => {
        const isFiltered = (searchTerm && searchTerm.trim()) || 
                          (advancedFilters && advancedFilters.rows && advancedFilters.rows.length > 0) ||
                          (activeFilters && !activeFilters.includes('All')) ||
                          (dashboardFilter);

        if (isFiltered) {
            // --- Client-side CSV Export (Filtered Data) ---
            if (!sidebarFilteredResults || sidebarFilteredResults.length === 0) {
                Swal.fire('No Data', 'No follow-ups to export with current filters.', 'info');
                return;
            }

            const headers = [
                "Lead ID",
                "Customer Name",
                "Mobile No",
                "Status",
                "Next Follow-up Date",
                "Subject/Remarks",
                "Agent",
                "Outcome",
                "Created On"
            ];

            const formatExcelDate = (dateString) => {
                if (!dateString || dateString === '0' || dateString === 'null') return '';
                const date = parseCustomDate(dateString);
                if (!date || isNaN(date.getTime())) return dateString;
                const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
                return `${String(date.getDate()).padStart(2, '0')}-${months[date.getMonth()]}-${date.getFullYear()}`;
            };

            const escapeCSV = (val) => {
                if (val === null || val === undefined) return '""';
                let str = String(val).replace(/"/g, '""');
                return `" ${str}"`;
            };

            const rows = sidebarFilteredResults.map(fu => {
                const maskedFu = maskObject({ ...fu });
                return [
                    maskedFu.LeadId || '',
                    maskedFu.PersonName || '',
                    maskedFu.MobileNo || '',
                    maskedFu.FollowupStatus || '',
                    formatExcelDate(maskedFu.NextStatusDate),
                    maskedFu.Remarks || '',
                    maskedFu.AgentName || '',
                    maskedFu.Outcome || '',
                    formatExcelDate(maskedFu.CreatedOn)
                ];
            });

            let csvContent = headers.map(h => `"${h}"`).join(",") + "\n";
            rows.forEach(row => {
                csvContent += row.map(escapeCSV).join(",") + "\n";
            });

            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `Followuplist_Filtered_${new Date().getTime()}.csv`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);

            Swal.fire({
                icon: 'success',
                title: 'Export Successful',
                text: 'Your filtered follow-ups have been exported.',
                timer: 2000,
                showConfirmButton: false
            });
            return;
        }

        // --- Original API Export (All Data) ---
        const userId = parseInt(localStorage.getItem('userId') || localStorage.getItem('UserId') || '34594', 10);
        const token = API_ENDPOINTS.TOKE.UNIVERSAL_TOKEN;

        const now = new Date();
        const twoYearsAgo = new Date(now); twoYearsAgo.setFullYear(now.getFullYear() - 2);
        const oneYearAhead = new Date(now); oneYearAhead.setFullYear(now.getFullYear() + 1);
        const fmt = (d, endOfDay = false) => {
            if (!d || isNaN(d.getTime())) return "";
            const dd = String(d.getDate()).padStart(2, '0');
            const mm = String(d.getMonth() + 1).padStart(2, '0');
            const yyyy = d.getFullYear();
            return `${yyyy}-${mm}-${dd}T${endOfDay ? '23:59:59' : '00:00:00'}`;
        };

        const isAll = activeFilters.includes('All');

        let exportFilterText = "";
        if (searchTerm && searchTerm.trim()) {
            const fuzzySearch = searchTerm.trim().replace(/'/g, "''").replace(/\s+/g, '%');
            exportFilterText = ` AND (PersonName LIKE '%${fuzzySearch}%' OR Remarks LIKE '%${fuzzySearch}%' OR MobileNo LIKE '%${fuzzySearch}%' OR CAST(LeadId AS VARCHAR) LIKE '%${fuzzySearch}%')`;
        }

        const payload = {
            Token: token,
            Details: {
                UserId: userId,
                FollowUpType: filters.FollowUpType || "0",
                IsAdmin: false,
                NextStatusFromDate: filters.NextStatusFromDate ? fmt(new Date(filters.NextStatusFromDate)) : fmt(twoYearsAgo),
                NextStatusToDate: filters.NextStatusToDate ? fmt(new Date(filters.NextStatusToDate), true) : fmt(oneYearAhead, true),
                CreatedFromDate: filters.CreatedFromDate ? fmt(new Date(filters.CreatedFromDate)) : fmt(twoYearsAgo),
                CreatedToDate: filters.CreatedToDate ? fmt(new Date(filters.CreatedToDate), true) : fmt(now, true),
                LeadId: filters.LeadId || "",
                Remarks: filters.Remarks || "",
                Overdue: isAll ? true : (filters.Overdue ?? true),
                DueToday: isAll ? true : (filters.DueToday ?? true),
                Scheduled: isAll ? true : (filters.Scheduled ?? true),
                NoFollowUp: isAll ? true : (filters.NoFollowUp ?? true),
                AssignedTo: parseInt(filters.AssignedTo || "0", 10),
                CreatedBy: parseInt(filters.CreatedBy || "0", 10),
                IsReAssign: filters.isReAssign || "0",
                CriteriaName: "",
                FilterText: exportFilterText,
                SearchTerm: searchTerm || "",
                TimeZonename: "India Standard Time"
            }
        };

        Swal.fire({
            title: 'Exporting All Records...',
            text: 'Please wait while we prepare your document',
            allowOutsideClick: false,
            showConfirmButton: false,
            didOpen: () => { Swal.showLoading(); }
        });

        try {
            const response = await fetch(Export_FollowUp, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            if (response.ok) {
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `Followuplist_Full_${new Date().getTime()}.csv`;
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
                document.body.removeChild(a);

                Swal.fire({
                    icon: 'success',
                    title: 'Export Successful',
                    text: 'Full export download has started.',
                    timer: 2000,
                    showConfirmButton: false
                });
            } else {
                const errorText = await response.text();
                Swal.fire('Error', errorText || 'Failed to export follow-ups', 'error');
            }
        } catch (err) {
            console.error('FollowUp Export error:', err);
            Swal.fire('Error', 'An unexpected error occurred during export.', 'error');
        }
    };

    // Handle filter changes
    const handleFilterChange = (newFilters) => {
        // Map UI filter keys from FollowUpFilterationActions to internal state keys
        const mappedFilters = { ...newFilters };

        if (newFilters.startDate) mappedFilters.NextStatusFromDate = newFilters.startDate;
        if (newFilters.endDate) mappedFilters.NextStatusToDate = newFilters.endDate;

        if (newFilters.status) {
            const s = newFilters.status.toLowerCase();
            if (s === 'completed') {
                mappedFilters.FollowupStatus = 'Completed';
                mappedFilters.Open = false;
            } else if (s === 'open') {
                mappedFilters.FollowupStatus = '';
                mappedFilters.Open = true;
            } else if (s === 'pending') {
                mappedFilters.FollowupStatus = 'Pending';
                mappedFilters.Open = false;
            } else {
                mappedFilters.FollowupStatus = 'All';
                mappedFilters.Open = false;
            }
        }

        if (newFilters.sortBy) {
            if (newFilters.sortBy === 'date_desc') mappedFilters.orderBy = 'Date (Newest First)';
            else if (newFilters.sortBy === 'date_asc') mappedFilters.orderBy = 'Date (Oldest First)';
            else if (newFilters.sortBy === 'name_asc') mappedFilters.orderBy = 'Name (A-Z)';
            else if (newFilters.sortBy === 'name_desc') mappedFilters.orderBy = 'Name (Z-A)';
        }

        if (newFilters.outcome) mappedFilters.outcomes = newFilters.outcome;

        setFilters(prev => ({ ...prev, ...mappedFilters }));
        setCurrentPage(1);
    };

    const handleStatusFilter = (status) => {
        if (status === 'ResetAll') {
            setActiveFilters(['All']);
            setSearchTerm('');
            setAdvancedFilters(null);
            setCustomSearchId(0);
            setFilters(prev => ({
                ...prev,
                Overdue: true,
                DueToday: true,
                Scheduled: true,
                NoFollowUp: true,
                NotCompleted: false,
                Open: false,
                FollowupStatus: '',
                NextStatusFromDate: '',
                NextStatusToDate: '',
                CreatedFromDate: '',
                CreatedToDate: '',
                LeadId: '',
                Remarks: ''
            }));
            setCurrentPage(1);
            return;
        }

        if (status === 'All') {
            setActiveFilters(['All']);
            return;
        }

        setActiveFilters(prev => {
            const current = Array.isArray(prev) ? prev : ['All'];
            if (current.includes(status)) {
                const updated = current.filter(s => s !== status);
                return updated.length === 0 ? ['All'] : updated;
            } else {
                const next = current.filter(s => s !== 'All');
                return [...next, status];
            }
        });
        setCurrentPage(1);
    };

    const handleShowAll = useCallback(() => {
        setFilters(prev => ({
            ...prev,
            Overdue: true,
            DueToday: true,
            Scheduled: true,
            NoFollowUp: true,
            NotCompleted: false,
            Open: false,
            FollowupStatus: '',
            NextStatusFromDate: '',
            NextStatusToDate: '',
            CreatedFromDate: '',
            CreatedToDate: ''
        }));
        setSearchTerm('');
        setAdvancedFilters(null);
        setCustomSearchId(0);
        setCurrentPage(1);
    }, []);

    const handleRowClick = (followup) => {
        // In grid view, clicking a card only updates the split-pane — no modal
        if (viewMode === 'grid') return;
        setSelectedFollowUp(followup);
        setInitialMode('interaction-history');
        setShowDetails(true);
    };

    const handleRelatedToClick = useCallback((e, followup) => {
        e.stopPropagation();
        // In grid view, do nothing — detail pane handles everything
        if (viewMode === 'grid') return;
        setViewingLeadId(followup.LeadId);
    }, [viewMode]);

    const handleCalendarEventClick = (event) => {
        const fullEvent = followUps.find(fu => (fu.FollowUpId || fu.ID || fu.Id).toString() === event.id.toString());
        if (fullEvent) {
            handleRowClick(fullEvent);
        }
    };

    const handleAddFollowup = useCallback((followup) => {
        setSelectedFollowUp(followup);
        setInitialMode('add_followup_premium');
        setShowDetails(true);
    }, []);

    const handleCloseDetails = () => {
        setShowDetails(false);
        setSelectedFollowUp(null);
    };

    // Reset page to 1 when filters or search change
    useEffect(() => {
        setCurrentPage(1);
        // Clear selected item when search or filters change to allow empty state to show in right panel
        setSelectedGridFollowUp(null);
    }, [searchTerm, advancedFilters]);


    const getPaginationRange = (current, total) => {
        if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
        if (current <= 4) return [1, 2, 3, 4, 5, '...', total];
        if (current >= total - 3) return [1, '...', total - 4, total - 3, total - 2, total - 1, total];
        return [1, '...', current - 1, current, current + 1, '...', total];
    };

    const handlePageChange = (pageNumber) => {
        if (pageNumber >= 1 && pageNumber <= totalPages) {
            setCurrentPage(pageNumber);
        }
    };



    // Calendar events (for calendar view)
    const calendarEvents = useMemo(() => {
        return filteredFollowUps.filter(f => f.NextStatusDate).map(fu => ({
            id: fu.ID || fu.FollowUpId,
            title: `${fu.Title || 'Follow-up'} - ${fu.PersonName || ''}`,
            start: fu.NextStatusDate,
            date: fu.NextStatusDate, // for yearly view compatibility
            status: fu._statusClass === 'overdue' ? 'overdue' : (fu._statusClass === 'due-today' ? 'upcoming' : 'open'),
            extendedProps: { ...fu },
        }));
    }, [filteredFollowUps]);


    const renderInteractionHistoryContent = (item) => {
        // If no item is provided, it means we are in a "No Data" state for the right panel
        if (!item || !item.id) {
            return (
                <Box sx={{ display: 'flex', flex: 1, alignItems: 'center', justifyContent: 'center', height: '100%', flexDirection: 'column', gap: '20px', padding: '40px', textAlign: 'center' }}>
                    <img src="/nodata.gif" alt="No record Found" style={{ width: '280px', marginBottom: '16px', opacity: 0.8 }} />
                    <Typography variant="h5" sx={{ fontWeight: 700, color: '#1e293b', mb: 1 }}>
                        No record Found.
                    </Typography>
                    <Typography sx={{ color: '#64748b', maxWidth: '400px' }}>
                        We couldn't find any records matching your current filters or search term.
                    </Typography>
                </Box>
            );
        }

        const fu = item._original || item;
        return (
            <div className="sidebar-content" style={{ flex: 1, padding: '20px', display: 'flex', flexDirection: 'column', overflowY: 'auto' }}>
                <div className="history-card-v4" style={{ background: 'white', borderRadius: '4px', padding: '0 20px 20px 20px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                    <div className="history-header-v4">
                        <h3 style={{ textTransform: 'capitalize' }}>interaction History</h3>
                        <button className="add-history-btn-v4" onClick={(e) => handleAction(e, fu, 'add_followup_premium')}>+</button>
                    </div>

                    <div className="lead-summary-v4" style={{ display: 'flex', alignItems: 'center', padding: '20px 0', borderBottom: '1px solid #f1f5f9', gap: '20px' }}>
                        <div
                            className="lead-avatar-v4"
                            style={{
                                width: '50px',
                                height: '50px',
                                borderRadius: '50%',
                                background: '#fff1f2',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: '#e11d48',
                                fontSize: '18px',
                                fontWeight: '700',
                                flexShrink: 0,
                                position: 'relative',
                                border: '1px solid #ffe4e6'
                            }}
                        >
                            {getInitials(fu.PersonName || fu.ClientName || historyLead?.PersonName)}
                        </div>
                        <div className="lead-meta-v4">
                            <h4 style={{ margin: '0 0 8px 0', fontSize: '15px', color: '#1e293b', fontWeight: '700' }}>{fu.PersonName || fu.ClientName || historyLead?.PersonName || 'Unknown'}</h4>
                            <div className="meta-row-v4" style={{ fontSize: '13px', color: '#64748b', display: 'flex', flexWrap: 'wrap', gap: '15px' }}>
                                <span><strong style={{ color: '#475569', fontWeight: '500' }}>Mobile No. :</strong> {fu.MobileNo || fu.Mobile || historyLead?.MobileNo || '-'}</span>
                                <span><strong style={{ color: '#475569', fontWeight: '500' }}>Lead No. :</strong> {fu.LeadNo || fu.LeadID || '-'}</span>
                            </div>
                        </div>
                    </div>

                    <div className="history-list-v4">
                        {historyLoading ? (
                            <Box sx={{ py: 10, display: 'flex', justifyContent: 'center' }}>
                                <Loader message="Loading interaction history..." />
                            </Box>
                        ) : interactionHistoryData.length > 0 ? (
                            interactionHistoryData.map((item, idx) => (
                                <div key={idx} className="history-item-v4">
                                    <div className="item-status-v4">{item.FollowupStatus || 'Callback'}</div>
                                    <div className="item-remarks-v4">{item.Remarks || '-'}</div>
                                    <div className="item-details-grid-v4">
                                        <div className="detail-col-v4">
                                            <div className="detail-row-v4">
                                                <span className="label-v4">Created Date:</span>
                                                <span className="value-v4">{formatDate(item.CreatedOn)}</span>
                                            </div>
                                            <div className="detail-row-v4">
                                                <span className="label-v4">Due Date:</span>
                                                <span className="value-v4">{formatDate(item.NextStatusDate)}</span>
                                            </div>
                                            <div className="detail-row-v4">
                                                <span className="label-v4">Amount:</span>
                                                <span className="value-v4">{item.AmountPaid || '0.00'}</span>
                                            </div>
                                        </div>
                                        <div className="detail-col-v4">
                                            <div className="detail-row-v4">
                                                <span className="label-v4">Assign To:</span>
                                                <span className="value-v4">{item.AssignedTo}</span>
                                            </div>
                                            <div className="detail-row-v4">
                                                <span className="label-v4">Created By:</span>
                                                <span className="value-v4">{item.CreatedBy}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 10, opacity: 0.8 }}>
                                <img src="/nodata.gif" alt="No History Found" style={{ width: '180px', marginBottom: '16px' }} />
                                <Typography sx={{ fontSize: '15px', fontWeight: 600, color: '#64748b' }}>No interaction history found.</Typography>
                            </Box>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    if (followupPerms && followupPerms.View === false) {
        return (
            <div className="followup-module-container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 'calc(100vh - 64px)', background: '#f8fafc' }}>
                <div style={{ background: 'white', padding: '40px', borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.08)', textAlign: 'center', maxWidth: '400px' }}>
                    <div style={{ width: '64px', height: '64px', background: '#fee2e2', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px' }}>
                        <X size={32} color="#ef4444" />
                    </div>
                    <h2 style={{ fontSize: '24px', fontWeight: 'bold', color: '#1e293b', marginBottom: '16px' }}>Access Denied</h2>
                    <p style={{ color: '#64748b', marginBottom: '32px', lineHeight: '1.5' }}>
                        You do not have permission to view the Follow-up Module. Please contact your administrator if you believe this is a mistake.
                    </p>
                    <div style={{ display: 'flex', gap: '16px', justifyContent: 'center' }}>
                        <button 
                            onClick={() => navigate('/dashboard')} 
                            style={{ padding: '10px 20px', background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '500' }}
                        >
                            Dashboard
                        </button>
                        <button 
                            onClick={() => { localStorage.clear(); navigate('/'); }} 
                            style={{ padding: '10px 20px', background: '#3b82f6', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '500' }}
                        >
                            Logout
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="followup-module-container" style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 64px)', background: viewingLeadId ? '#fff' : '#f8fafc', position: 'relative', overflow: 'hidden' }}>
            <div className="module-content-wrapper" style={{ flex: 1, overflowY: 'auto', minHeight: 0, display: 'flex', flexDirection: 'column', width: '100%' }}>
                <SubHeader
                    pageName="Follow-ups"
                    pageCode="PAG10039"
                    title="Follow-up "
                    searchQuery={searchTerm}
                    setSearchQuery={setSearchTerm}
                    searchPlaceholder="Search follow-up..."
                    viewMode={viewMode}
                    setViewMode={setViewMode}
                    showGrid={true}
                    showTable={true}
                    showCalendar={true}
                    totalCount={sidebarFilteredResults.length}
                    filterCount={customSearchId > 0 ? 1 : 0}
                    activeFilters={activeFilters}
                    onAddClick={followupPerms?.Add ? () => handleAddFollowup({}) : null}
                    showExport={!!followupPerms?.Export}
                    onExportClick={followupPerms?.Export ? handleExportTasks : null}
                    showCustomExport={!!followupPerms?.Export}
                    onCustomExportClick={followupPerms?.Export ? () => setIsCustomDownloadOpen(true) : null}
                    showSettings={true}
                    advanceFilterFields={followUpFields}
                    onAdvanceFilterSubmit={followupPerms?.Search ? setAdvancedFilters : null}
                    onAdvanceFilterClear={() => setAdvancedFilters(null)}
                    showSearch={!!followupPerms?.Search}
                    showAdvanceFilter={!!followupPerms?.Search}
                    showCustomSearch={!!followupPerms?.Search}
                    customSearchId={customSearchId}
                    onCustomSearchSubmit={followupPerms?.Search ? (id) => { setCustomSearchId(id); setCurrentPage(1); } : null}
                    onCustomSearchClear={() => { setCustomSearchId(0); setCurrentPage(1); }}

                />

                <FollowUpFilterationActions
                    viewMode={viewMode}
                    onExport={handleExportTasks}
                    onCustomDownload={() => setIsCustomDownloadOpen(true)}
                    showLeadProfile={showLeadProfile}
                    onCloseLeadProfile={() => setShowLeadProfile(false)}
                    leadData={selectedProfileLead}
                    showEditModal={showEditModal}
                    onCloseEditModal={() => setShowEditModal(false)}
                    editLeadData={selectedEditLead}
                />


                {/* Global Premium Dashboard Scorecards (Now below Search/Title) */}
                {!viewingLeadId && (
                    <div style={{ padding: '0 24px', background: '#fff', position: 'relative', zIndex: 50, overflow: 'visible' }}>
                        <PremiumModuleCards
                            stats={stats}
                            onStatusClick={handleStatusFilter}
                            activeFilters={activeFilters}
                            moduleName="Follow-Up"
                            loading={loading}
                            customCards={[
                                {
                                    title: stats.total || 0,
                                    subtitle: 'Total Follow Ups',
                                    icon: 'ri:list-check',
                                    color: 'info',
                                    key: 'All'
                                },
                                {
                                    title: stats.dueToday || 0,
                                    subtitle: 'Due Today',
                                    icon: 'ri:calendar-check-line',
                                    color: 'success',
                                    key: 'DueToday'
                                },
                                {
                                    title: stats.overdue || 0,
                                    subtitle: 'Overdue',
                                    icon: 'ri:error-warning-line',
                                    color: 'error',
                                    key: 'Overdue'
                                },
                                {
                                    title: stats.scheduled || 0,
                                    subtitle: 'Scheduled',
                                    icon: 'ri:time-line',
                                    color: 'warning',
                                    key: 'Scheduled'
                                },
                                {
                                    title: stats.noFollowUp || 0,
                                    subtitle: 'No Followup',
                                    icon: 'ri:calendar-line',
                                    color: 'secondary',
                                    key: 'NoFollowUp'
                                }
                            ]}
                        />
                    </div>
                )}


                {/* Dashboard filter indicator */}
                {!viewingLeadId && filterLabel && (
                    <div style={{ padding: '8px 24px', background: '#eff6ff', borderBottom: '1px solid #bfdbfe' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', fontWeight: '600', color: '#1e40af' }}>
                            <Filter size={14} />
                            <span>Filtered by Dashboard: {filterLabel}</span>
                            <button
                                onClick={clearDashboardFilter}
                                className="clear-filter-circle"
                            >
                                <Plus size={14} style={{ transform: 'rotate(45deg)' }} />
                            </button>
                        </div>
                    </div>
                )}
                {/* Main Content Area */}
                <div className="followup-content-area" style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', position: 'relative' }}>
                    {viewingLeadId ? (
                        <Box sx={{ flex: 1, height: '100%', overflow: 'hidden' }}>
                            <CommonLeadDetails
                                leadId={viewingLeadId}
                                onBack={() => setViewingLeadId(null)}
                                module="followup"
                                maskObject={maskObject}
                                isHiddenField={isHiddenField}
                            />
                        </Box>
                    ) : (!loading && !isInitialLoad && sidebarFilteredResults.length === 0) ? (
                        <Box sx={{ display: 'flex', flex: 1, alignItems: 'center', justifyContent: 'center', height: '100%', flexDirection: 'column', gap: '20px', padding: '40px', textAlign: 'center' }}>
                            <img src="/nodata.gif" alt="No record Found" style={{ width: '280px', marginBottom: '16px', opacity: 0.8 }} />
                            <Typography variant="h5" sx={{ fontWeight: 700, color: '#1e293b', mb: 1 }}>
                                No record Found.
                            </Typography>
                            <Typography sx={{ color: '#64748b', maxWidth: '400px' }}>
                                We couldn't find any records matching your current filters or search term.
                            </Typography>
                        </Box>
                    ) : (
                        <>

                            {viewMode === 'table' ? (
                                <FollowUpList
                                    onAction={handleAction}
                                    onViewDetail={(fu) => {
                                        setSelectedFollowUp(fu);
                                        setInitialMode('interaction-history');
                                        setShowDetails(true);
                                    }}
                                    onViewLead={(leadId) => {
                                        setViewingLeadId(leadId);
                                    }}
                                    // Optimized Data & Pagination Props
                                    data={displayedFollowUps.map(fu => maskObject(fu))}
                                    totalRecords={searchTerm ? filteredFollowUps.length : totalRecords}
                                    loading={loading}
                                    isInitialLoad={isInitialLoad}
                                    currentPage={currentPage}
                                    itemsPerPage={itemsPerPage}
                                    onPageChange={handlePageChange}
                                    stats={stats}
                                    crmOperationPerms={followupPerms}
                                    isHiddenField={isHiddenField}
                                    // Other Common Props
                                    filters={filters}
                                    handleStatusFilter={handleStatusFilter}
                                    handleShowAll={handleShowAll}
                                    searchTerm={searchTerm}
                                    setSearchTerm={setSearchTerm}
                                    viewMode={viewMode}
                                    setViewMode={setViewMode}
                                    onExport={handleExportTasks}
                                    onCustomDownload={() => setIsCustomDownloadOpen(true)}
                                />
                            ) : viewMode === 'grid' ? (
                                <GridMainPanelLayout
                                    data={sidebarFilteredResults.map(maskObject).map(fu => ({
                                        id: fu.ID || fu.FollowUpId,
                                        title: fu.TaskName || `${fu.FollowupStatus || 'Follow-up'}`,
                                        date: fu.FollowupDate,
                                        leadId: fu.LeadId,
                                        personName: fu.PersonName || fu.ClientName,
                                        _original: fu,
                                        _statusClass: fu._statusClass,
                                        _badgeText: fu._badgeText
                                    }))}
                                    getCompletionBadge={(item) => getFollowUpBadge(item._original || item)}
                                    loading={loading}
                                    isInitialLoad={isInitialLoad}
                                    type="followup"
                                    getInitials={getInitials}
                                    formatDate={formatDate}
                                    hideSelection={!(followupPerms && (
                                        followupPerms.MassUpdate || followupPerms.MassDelete || 
                                        followupPerms.MassSMS || followupPerms.MassEmail || 
                                        followupPerms.MassAssign || followupPerms.MassBroadcast
                                    ))}
                                    onSelectItem={(item) => setSelectedGridFollowUp(item._original)}
                                    selectedItem={selectedGridFollowUp ? {
                                        id: selectedGridFollowUp.ID || selectedGridFollowUp.FollowUpId,
                                        _original: selectedGridFollowUp
                                    } : null}
                                    currentPage={currentPage}
                                    totalPages={totalPages}
                                    onPageChange={handlePageChange}
                                    onViewLead={(leadId) => setViewingLeadId(leadId)}
                                    renderDetailContent={renderInteractionHistoryContent}
                                    yearlyTooltipRenderer={(events, counts) => {
                                        const names = events.slice(0, 5).map(e => {
                                            const fu = e.extendedProps || {};
                                            return `- ${fu.FollowupStatus || 'Follow-up'} (${fu.PersonName || 'Unknown'})`;
                                        }).join('\n');
                                        const extra = events.length > 5 ? `\n... and ${events.length - 5} more` : '';

                                        return `${events.length} Follow-up(s):\n${names}${extra}\n\nSummary:\n- Overdue: ${counts.overdue}\n- Due Today: ${counts.upcoming}\n- Scheduled: ${counts.open}`;
                                    }
                                    }
                                    events={calendarEvents}
                                    onEventClick={(info) => {
                                        // info might be the event object directly if using our custom renderer inside default DynamicCalendar logic
                                        // or the FullCalendar info object if supported. 
                                        // Our DynamicCalendar.jsx passes the event object directly to onEventClick.
                                        setSelectedFollowUp(info.extendedProps || info);
                                        setInitialMode('view');
                                        setShowDetails(true);
                                    }}
                                />
                            ) : viewMode === 'calendar' ? (
                                <div className="calendar-view-wrapper" style={{ flex: 1, overflow: 'hidden', position: 'relative', background: '#fff' }}>
                                    {loading && <Loader message="Syncing calendar..." />}
                                    <DynamicCalendar
                                        headerLegend={
                                            <Stack direction="row" spacing={2.5} sx={{ mt: 1, flexWrap: 'wrap', gap: '12px' }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#2563eb' }} />
                                                    <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#475569' }}>Open</Typography>
                                                </Box>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#f97316' }} />
                                                    <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#475569' }}>Upcoming</Typography>
                                                </Box>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#ef4444' }} />
                                                    <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#475569' }}>Overdue</Typography>
                                                </Box>
                                            </Stack>
                                        }
                                        events={calendarEvents}
                                        eventRenderer={renderCalendarEvent}
                                        onEventClick={handleCalendarEventClick}
                                        initialView="month"
                                        initialDate={new Date()}
                                        entityLabel="Follow-up"
                                    />
                                </div>
                            ) : null}
                        </>
                    )}
                </div>
            </div>


            {/* Details Panel Popup - Only for Table/Calendar view, or for Actions in Grid view */}
            <Panels
                isOpen={showDetails && (viewMode !== 'grid' || (initialMode !== 'view' && initialMode !== 'interaction-history'))}
                onClose={handleCloseDetails}
                title="Follow Up"
            >
                {selectedFollowUp && (
                    <FollowUpDetails
                        crmOperationPerms={followupPerms}
                        followup={selectedFollowUp}
                        onClose={handleCloseDetails}
                        onRefresh={onRefresh}
                        initialMode={initialMode}
                        isInline={true}
                    />
                )}
            </Panels>

            {/* Custom Download Modal */}
            {isCustomDownloadOpen && (
                <div className="custom-download-overlay" onClick={() => setIsCustomDownloadOpen(false)}>
                    <div className="custom-download-panel" onClick={(e) => e.stopPropagation()}>
                        <div className="custom-download-header">
                            <h3>Custom Download</h3>
                            <button className="close-panel-btn" onClick={() => setIsCustomDownloadOpen(false)}>&times;</button>
                        </div>
                        <div className="custom-download-body">
                            <div className="setting-select-row">
                                <select
                                    className="setting-dropdown"
                                    value={selectedCustomSetting}
                                    onChange={(e) => {
                                        const sid = e.target.value;
                                        setSelectedCustomSetting(sid);
                                        const s = customSettings.find(st => st.Id.toString() === sid);
                                        if (s && s.CsvColumns) setSelectedExportFields(s.CsvColumns.split(','));
                                        else setSelectedExportFields([]);
                                    }}
                                >
                                    <option value="">Select Save Setting</option>
                                    {customSettings.map(s => <option key={s.Id} value={s.Id}>{s.SettingName}</option>)}
                                </select>
                            </div>
                            <div className="field-search-box">
                                <input
                                    type="text"
                                    placeholder="Search fields..."
                                    className="field-search-input"
                                    value={fieldSearchTerm}
                                    onChange={(e) => setFieldSearchTerm(e.target.value)}
                                />
                                <Search size={16} className="field-search-icon" />
                            </div>
                            <div className="fields-list">
                                {customExportFields.filter(f => f.ExposedName.toLowerCase().includes(fieldSearchTerm.toLowerCase())).map((f, i) => (
                                    <label key={i} className="field-item">
                                        <input
                                            type="checkbox"
                                            checked={selectedExportFields.includes(f.ExposedName)}
                                            onChange={() => {
                                                setSelectedExportFields(prev => prev.includes(f.ExposedName) ? prev.filter(x => x !== f.ExposedName) : [...prev, f.ExposedName]);
                                            }}
                                        />
                                        <span className="field-name">{f.ExposedName}</span>
                                    </label>
                                ))}
                            </div>
                            <div className="save-setting-row">
                                <input
                                    type="text"
                                    placeholder="Save Setting As..."
                                    className="save-setting-input"
                                    value={exportSettingName}
                                    onChange={(e) => setExportSettingName(e.target.value)}
                                />
                                <button className="save-settings-btn" onClick={handleSaveExportSetting}>Save Setting</button>
                            </div>
                        </div>
                        <div className="custom-download-footer">
                            <button
                                className="final-download-btn"
                                onClick={handleCustomDownloadSubmit}
                                disabled={isDownloading}
                            >
                                {isDownloading ? 'Processing...' : 'Download'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Entity Edit Modal */}
            {showEntityEdit && (
                <EntityEdit
                    editingEntity={selectedEditLead}
                    onCloseEntity={() => {
                        setShowEntityEdit(false);
                        setSelectedEditLead(null);
                    }}
                />
            )}

            {/* Centralized Call Widget */}
            <CallWidget
                show={showCallWidget}
                onClose={() => setShowCallWidget(false)}
                toMobileNo={callingLead?.__original?.MobileNo || callingLead?.MobileNo || ''}
                entityId={callingLead?.__original?.LeadId || callingLead?.LeadId || callingLead?.ID || ''}
                entityName="lead"
                personName={callingLead?.__original?.PersonName || callingLead?.PersonName || ''}
                personImage={callingLead?.Image}
                triggerRect={callTriggerRect}
                pageCode="PAG10372#PAG10039"
            />


            {/* Scorecard Hover Popup */}
            {hoveredLeadId && (
                <div
                    className="scorecard-hover-popup"
                    style={{
                        position: 'absolute',
                        top: `${popupPosition.top}px`,
                        left: `${popupPosition.left}px`,
                        transform: 'translate(-50%, -100%)',
                        zIndex: 10000,
                        pointerEvents: 'none'
                    }}
                >
                    <div className="scorecard-glass-card">
                        {isScorecardLoading ? (
                            <div className="scorecard-loading-state">
                                <Loader message="Loading details..." size="small" />
                            </div>
                        ) : scorecardData ? (
                            <div className="scorecard-content">
                                <div className="scorecard-header">
                                    <h4 className="scorecard-person-name">{scorecardData.PersonName || 'Unknown Lead'}</h4>
                                    <p className="scorecard-company-name">{scorecardData.CompanyName || 'No Company'}</p>
                                </div>
                                <div className="scorecard-lead-since">
                                    Lead since {scorecardData.CreatedOn || 'N/A'}
                                </div>
                                <div className="scorecard-summary-row">
                                    <span style={{ fontSize: '13px', color: '#666' }}>
                                        Lead score <span style={{ color: scorecardData.Profit_Loss === 'Green_Up' ? '#27ae60' : '#ef4444', fontWeight: '600' }}>
                                            {scorecardData.Profit_Loss === 'Green_Up' ? '▲' : '▼'}{scorecardData.ScorePRCNTG || '0'}%
                                        </span> in last 7 days. Top scoring factor(s):
                                    </span>
                                </div>
                                <div className="scorecard-main-content">
                                    <div className="scorecard-circle-badge">
                                        <div className="score-value">{scorecardData.CurrentScore || '0'}</div>
                                    </div>
                                    <div className="scorecard-factors-list">
                                        {scorecardData.Top3ScoringFactors ? (
                                            scorecardData.Top3ScoringFactors.split('#').map((factor, i) => (
                                                <div key={i} className="scorecard-factor-item">
                                                    <span className="scorecard-factor-icon" style={{ color: (scorecardData.Top3_ProfileLoss || '').split('#')[i] === 'Green_Up' ? '#27ae60' : '#ef4444' }}>
                                                        {(scorecardData.Top3_ProfileLoss || '').split('#')[i] === 'Green_Up' ? '▲' : '▼'}
                                                    </span>
                                                    <span className="scorecard-factor-text">{factor}</span>
                                                </div>
                                            ))
                                        ) : (
                                            <div className="scorecard-empty-state">No scoring factors found</div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="scorecard-empty-state">No scorecard data available</div>
                        )}
                    </div>
                </div>
            )}
            {/* Centralized Lead Details Panel removed from here and moved inside content area */}
        </div> /* End followup-module-container */
    );
};

export default FollowUpModule;


